import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, Box, Paper, Typography, Button } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { selectTaskSearchData } from '@AMIEWEB/Tasks/store/Tasks.selectors';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { FormProvider, useForm } from 'react-hook-form';
import { SearchTasksChips } from '@AMIEWEB/GlobalSearch/Tasks/SearchTasksChips';
import { TaskSearchFilterName } from '@AMIEWEB/Tasks/TaskManagement/TaskFilters/TaskFilterUtilities';
import {
  defaultFilterValues as taskDefaultFilterValues,
  taskSearchFilterName,
} from '@AMIEWEB/GlobalSearch/Tasks/SearchUtils';
import { initialUnitAggregateSearch } from 'app/ComponentLibrary/Filter/UnitSearch/Index';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { usePromiseTracker } from 'react-promise-tracker';
import { taskDetailsActions } from '@AMIEWEB/Tasks/store/Tasks.redux';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { getTasksInitialSortedColumn, TASKS_DEFAULT_PAGE_SIZE } from '@AMIEWEB/GlobalSearch/SearchResultsPage';
import { hasTasksRequiredFilters, transformToTaskSearchQuery } from '@AMIEWEB/GlobalSearch/Tasks/taskSearchQueryHelpers';
import {
  fromGridSortModel,
  isServerSortableField,
  toGridSortModel,
} from '@AMIEWEB/GlobalSearch/Tasks/taskSearchSortHelpers';
import { IPlacementDetails } from 'app/models/Placement/PlacementDetails';
import { ModsLinkTaskSearchFilters } from './ModsLinkTaskSearchFilters';
import { revalidateChipSelection } from '@AMIEWEB/GlobalSearch/helper';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { buildModsLinkTaskSearchRows } from './modsLinkTaskSearchGridUtils';
import {
  getModsLinkTaskSearchColumnDefs,
  getModsLinkTaskSearchGroupingColDef,
} from './modsLinkTaskSearchColumns';
import { ModsLinkTaskSelectionContext } from './ModsLinkTaskSearchGridSelection';
import { ModsLinkTaskSearchGridPagination } from './ModsLinkTaskSearchGridPagination';
import { selectUser } from 'oidc/user.selectors';
import { reconcileSelfEmployeeNameEntries } from 'oidc/selfEmployeeNameReconciler';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { RootState } from 'types';
import { IXGridBulkActionButton, XGridToolbar } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { getSingleBooleanFilterValue } from '@AMIEWEB/GlobalSearch/Tasks/taskSearchBooleanFilterUtils';

const useModsLinkTaskSearchLayoutStyles = makeStyles()((theme) => ({
  root: {
    minHeight: 'auto',
    padding: '12px',
  },
  mainPanelRoot: {
    width: '100%',
  },
  container: {
    flexWrap: 'nowrap',
  },
}));

interface ModsLinkTaskSearchWrapperProps {
  foundPlacement: IPlacementDetails;
  isOpen?: boolean;
}

export const ModsLinkTaskSearchWrapper: React.FC<ModsLinkTaskSearchWrapperProps> = ({
  foundPlacement,
  isOpen = true,
}) => {
  const { classes } = useModsLinkTaskSearchLayoutStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const taskSearchData = useSelector(selectTaskSearchData);
  const gridState = useSelector(selectGridState);
  const { userInfo } = useSelector(selectUser);
  const gridTag = 'ModsLinkTaskGrid-tasks';
  const gridPreferences = useSelector((state: RootState) =>
    state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag),
  );
  const gridStateRef = useRef(gridState);
  const apiRef = useGridApiRef();
  const [selectedTaskRowId, setSelectedTaskRowId] = useState<string | null>(null);
  const selectedTaskRowIdRef = useRef<string | null>(null);
  const { promiseInProgress: isSearchLoading } = usePromiseTracker({ area: 'global-search-call', delay: 0 });
  const loading = isSearchLoading;
  const placementBrandId = foundPlacement?.header?.brandId;

  useEffect(() => {
    gridStateRef.current = gridState;
  }, [gridState]);

  useEffect(() => {
    selectedTaskRowIdRef.current = selectedTaskRowId;
  }, [selectedTaskRowId]);

  const getSearchResults = useCallback(
    ({ pageNumber, pageSize, sortModel }) => {
      const currentGridState = gridStateRef.current;
      const baseValues = currentGridState?.filterAttributes?.filters
        ? Object.fromEntries(currentGridState.filterAttributes.filters)
        : {};
      const taskSearchQuery = transformToTaskSearchQuery(
        baseValues,
        currentGridState?.filterAttributes?.selectedChipFilters,
        pageNumber || currentGridState?.pageNumber || 1,
        pageSize || currentGridState?.pageSize || TASKS_DEFAULT_PAGE_SIZE,
        getTasksInitialSortedColumn(sortModel ?? currentGridState?.sortedColumn),
      );

      if (!hasTasksRequiredFilters(taskSearchQuery)) {
        dispatch(
          globalActions.setBanner({
            severity: 'warning',
            message:
              'Please apply one or more search filters: From, Recipients, Category, Facility, OID, PID, Candidate, Unit',
            justify: 'start',
          }),
        );
        dispatch(taskDetailsActions.setTaskSearchData(null));
        return;
      }

      dispatch(globalActions.closeBanner());
      dispatch(taskDetailsActions.getTasksListAction(taskSearchQuery));
    },
    [dispatch],
  );

  const buildOption = useCallback((value?: number | string | null, name?: string) => {
    if (value == null || value === '') {
      return [];
    }
    return [
      {
        id: value,
        value,
        name: name ?? String(value),
      },
    ];
  }, []);

  const absoluteDefaultValues = useMemo(() => {
    const candidateId = foundPlacement?.header?.candidate?.id;
    const candidateName = [foundPlacement?.header?.candidate?.firstName, foundPlacement?.header?.candidate?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const placementId = foundPlacement?.header?.placementId;
    const orderId = foundPlacement?.header?.orderId;
    const facilityId = foundPlacement?.header?.facility?.id;
    const facilityName = foundPlacement?.header?.facility?.name;
    const candidateSelections = candidateId
      ? [
          {
            id: candidateId,
            value: candidateId,
            candidateId,
            name: candidateName || String(candidateId),
          },
        ]
      : [];

    return {
      [TaskSearchFilterName.assignedTo]: [],
      [TaskSearchFilterName.taskCategory]: [],
      [TaskSearchFilterName.taskName]: [],
      [TaskSearchFilterName.status]: taskDefaultFilterValues[taskSearchFilterName.status],
      [TaskSearchFilterName.dueDate]: null,
      sentDateRange: null,
      followUpFilter: [],
      followUpDateRange: null,
      priorities: [],
      optionApprovalFilter: [],
      flaggedFilter: [],
      coverageFilter: [],
      [TaskSearchFilterName.candidateAggregate]:
        candidateSelections.length > 0
          ? {
              ...initialCandidateAggregateSearch,
              selections: candidateSelections,
            }
          : initialCandidateAggregateSearch,
      [TaskSearchFilterName.placementId]: buildOption(placementId),
      [TaskSearchFilterName.orderId]: buildOption(orderId),
      [TaskSearchFilterName.facility]: [],
      [TaskSearchFilterName.facilityId]: buildOption(facilityId, facilityName),
      [TaskSearchFilterName.unit]: initialUnitAggregateSearch,
      [TaskSearchFilterName.createdBy]: [],
      [TaskSearchFilterName.recipients]: [
        {
          id: userInfo?.employeeId,
          value: userInfo?.employeeId,
          name: `${userInfo?.firstName ?? ''} ${userInfo?.lastName ?? ''}`.trim(),
        },
      ],
    };
  }, [buildOption, foundPlacement, userInfo?.employeeId, userInfo?.firstName, userInfo?.lastName]);

  const formMethods = useForm({ defaultValues: absoluteDefaultValues });
  const { handleSubmit, reset } = formMethods;

  // Sync form from grid state when filters are set (e.g. reload/session restore) so dropdowns match chips
  const filterAttributes = gridState?.filterAttributes;
  const filtersFromState = filterAttributes?.filters;

  const filtersSearchEffectKey = useMemo(() => {
    if (!Array.isArray(filtersFromState)) return '';
    try {
      return JSON.stringify(filtersFromState);
    } catch {
      return String(filtersFromState.length);
    }
  }, [filtersFromState]);

  const selectedChipFiltersFromState = filterAttributes?.selectedChipFilters;
  const selectedChipFiltersSearchEffectKey = useMemo(() => {
    try {
      return JSON.stringify(selectedChipFiltersFromState ?? null);
    } catch {
      return 'selectedChipFilters';
    }
  }, [selectedChipFiltersFromState]);

  const isTaskGridState =
    filterAttributes?.associatedGridName === gridTag &&
    Array.isArray(filtersFromState) &&
    filtersFromState.length > 0;
  const stateValues = useMemo<Record<string, unknown> | null>(
    () =>
      isTaskGridState && Array.isArray(filtersFromState)
        ? Object.fromEntries(filtersFromState as Array<[string, unknown]>)
        : null,
    [filtersFromState, isTaskGridState],
  );
  const activeFlaggedFilter = useMemo(
    () => getSingleBooleanFilterValue(stateValues?.flaggedFilter),
    [stateValues],
  );
  const activeCoverageFilter = useMemo(
    () => getSingleBooleanFilterValue(stateValues?.coverageFilter),
    [stateValues],
  );

  // Reconcile self-name on form values for Recipients / Created By; see
  // `oidc/selfEmployeeNameReconciler` for shared logic. ADO 506080 (Bug 2).
  const reconcileSelfEmployeeName = useCallback(
    (entries: unknown) =>
      reconcileSelfEmployeeNameEntries(entries, {
        employeeId: userInfo?.employeeId,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
      }),
    [userInfo?.employeeId, userInfo?.firstName, userInfo?.lastName],
  );

  useEffect(() => {
    if (isTaskGridState && filtersFromState) {
      const merged: Record<string, unknown> = { ...absoluteDefaultValues, ...stateValues };
      merged[TaskSearchFilterName.recipients] = reconcileSelfEmployeeName(
        merged[TaskSearchFilterName.recipients],
      );
      merged[TaskSearchFilterName.createdBy] = reconcileSelfEmployeeName(
        merged[TaskSearchFilterName.createdBy],
      );
      reset(merged);
    }
  }, [
    absoluteDefaultValues,
    filtersFromState,
    gridTag,
    isTaskGridState,
    reconcileSelfEmployeeName,
    reset,
    stateValues,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    dispatch(taskDetailsActions.setTaskSearchData(null));
    dispatch(gridStateActions.setFiltersExpanded(false));
    dispatch(placementModsAction.setSelectedTaskForMod(null));

    const selectedChipFilters = revalidateChipSelection(absoluteDefaultValues, {});
    dispatch(gridStateActions.setGrid(gridTag));
    dispatch(gridStateActions.setPageSize(TASKS_DEFAULT_PAGE_SIZE));
    dispatch(gridStateActions.setPageNumber(1));
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(absoluteDefaultValues),
        selectedChipFilters,
        associatedGridName: gridTag,
        dirtyFields: {},
      }),
    );
    dispatch(gridStateActions.setSelectedFilters(absoluteDefaultValues));
    dispatch(gridStateActions.setSelectedChips(selectedChipFilters));
    dispatch(globalActions.closeBanner());
    reset(absoluteDefaultValues);
    selectedTaskRowIdRef.current = null;
    setSelectedTaskRowId(null);
  }, [absoluteDefaultValues, dispatch, gridTag, isOpen, reset]);

  const onApplyFilter = (values: any, newSelectedChipFilters?: any) => {
    const selectedChipFilters =
      newSelectedChipFilters ||
      revalidateChipSelection(values, gridState.filterAttributes?.selectedChipFilters || {});
    dispatch(gridStateActions.setPageNumber(1));
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(values),
        selectedChipFilters,
        associatedGridName: gridTag,
        dirtyFields: {},
      }),
    );
    dispatch(gridStateActions.setSelectedFilters(values));
    dispatch(gridStateActions.setSelectedChips(selectedChipFilters));
  };

  const rows = useMemo(
    () =>
      buildModsLinkTaskSearchRows(taskSearchData?.items, {
        activeFlaggedFilter,
        activeCoverageFilter,
      }),
    [activeCoverageFilter, activeFlaggedFilter, taskSearchData?.items],
  );

  const handleSelectTask = useCallback(
    row => {
      const taskId = Number(row?.taskID ?? row?.taskId ?? 0);
      const rootTaskId = Number(row?.rootTaskID ?? row?.rootTaskId ?? taskId ?? 0);
      if (!taskId || !rootTaskId) {
        selectedTaskRowIdRef.current = null;
        setSelectedTaskRowId(null);
        dispatch(placementModsAction.setSelectedTaskForMod(null));
        return;
      }

      const rowIdStr = String(row.id);
      const prev = selectedTaskRowIdRef.current;
      const nextValue = prev === rowIdStr ? null : rowIdStr;
      selectedTaskRowIdRef.current = nextValue;
      setSelectedTaskRowId(nextValue);
      dispatch(
        placementModsAction.setSelectedTaskForMod(nextValue ? { taskId, rootTaskId } : null),
      );
    },
    [dispatch],
  );

  const modsLinkTaskSelectionValue = useMemo(
    () => ({ selectedTaskRowId, onSelectTask: handleSelectTask }),
    [selectedTaskRowId, handleSelectTask],
  );

  const columns = useMemo(
    () =>
      getModsLinkTaskSearchColumnDefs({
        theme,
        placementBrandId,
      }),
    [placementBrandId, theme],
  );

  const groupingColDef = useMemo(() => getModsLinkTaskSearchGroupingColDef(theme), [theme]);

  // Tree data path function
  const getTreeDataPath = React.useCallback((row: any) => row.path, []);

  const defaultModsVisibility: Record<string, boolean> = useMemo(
    () => ({
      affiliateContactID: false,
      facilityContactID: false,
    }),
    [],
  );

  const columnVisibilityModel = useMemo(
    () => ({
      ...defaultModsVisibility,
      ...(gridState.columnVisibilityModel || {}),
    }),
    [gridState.columnVisibilityModel, defaultModsVisibility],
  );

  const [customColumns, setCustomColumns] = React.useState(false);

  const hasCustomisedColumns = React.useCallback(
    (currentModel: Record<string, boolean>) => {
      const allColumns = apiRef.current?.getAllColumns() ?? [];
      for (const col of allColumns) {
        const isVisibleDefault = defaultModsVisibility[col.field] !== false;
        const isVisibleCurrent = currentModel[col.field] !== false;
        if (isVisibleDefault !== isVisibleCurrent) return true;
      }
      return false;
    },
    [apiRef, defaultModsVisibility],
  );

  const handleResetColumns = React.useCallback(() => {
    const allColumns = apiRef.current?.getAllColumns() ?? [];
    const resetModel: Record<string, boolean> = {};
    allColumns.forEach(col => {
      resetModel[col.field] = defaultModsVisibility[col.field] !== false;
    });
    dispatch(gridStateActions.setColumnVisibilityModel(resetModel));
    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: gridTag,
        value: {
          ...(gridPreferences?.value || {}),
          columnVisibilityModel: resetModel,
        },
      }),
    );
    setCustomColumns(false);
  }, [apiRef, defaultModsVisibility, dispatch, gridTag, gridPreferences?.value]);

  const hasHydratedVisibility = React.useRef(false);
  React.useEffect(() => {
    if (hasHydratedVisibility.current) return;
    const savedModel = gridPreferences?.value?.columnVisibilityModel;
    if (savedModel) {
      hasHydratedVisibility.current = true;
      const mergedModel = {
        ...defaultModsVisibility,
        ...savedModel,
      };
      dispatch(gridStateActions.setColumnVisibilityModel(mergedModel));
      setCustomColumns(hasCustomisedColumns(mergedModel));
    }
  }, [gridPreferences?.value?.columnVisibilityModel, dispatch, defaultModsVisibility, hasCustomisedColumns]);

  React.useEffect(() => {
    dispatch(gridStateActions.setGrid(gridTag));
    if (!gridState.pageSize) {
      dispatch(gridStateActions.setPageSize(TASKS_DEFAULT_PAGE_SIZE));
    }
    if (!gridState.pageNumber) {
      dispatch(gridStateActions.setPageNumber(1));
    }
  }, [dispatch, gridTag, gridState.pageSize, gridState.pageNumber]);

  // Get total row count from paging
  const rowCount = taskSearchData?.paging?.total || 0;
  // Get current page and page size from gridState
  const currentPage = gridState.pageNumber ? gridState.pageNumber - 1 : 0; // Convert to 0-based for DataGrid
  const currentPageSize = gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE;

  // Expand all tree nodes by default when rows change
  React.useEffect(() => {
    if (!rows || rows.length === 0) return;
    const allIds = apiRef.current?.getAllRowIds?.();
    allIds?.forEach((id: any) => {
      apiRef.current?.setRowChildrenExpansion(id, true);
    });
  }, [rows, apiRef]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (
      filterAttributes?.associatedGridName === gridTag &&
      Array.isArray(filterAttributes?.filters) &&
      filterAttributes.filters.length > 0
    ) {
      const currentGridState = gridStateRef.current;
      getSearchResults({
        pageNumber: currentGridState.pageNumber || 1,
        pageSize: currentGridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
        sortModel: currentGridState.sortedColumn,
      });
    }
  }, [
    filterAttributes?.associatedGridName,
    filtersSearchEffectKey,
    getSearchResults,
    gridTag,
    isOpen,
    selectedChipFiltersSearchEffectKey,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      dispatch(gridStateActions.setPageNumber(newPage + 1));
      getSearchResults({
        pageNumber: newPage + 1,
        pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
        sortModel: gridState.sortedColumn,
      });
    },
    [dispatch, getSearchResults, gridState.pageSize, gridState.sortedColumn],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      dispatch(gridStateActions.setPageSize(newPageSize));
      dispatch(gridStateActions.setPageNumber(1));
      getSearchResults({
        pageNumber: 1,
        pageSize: newPageSize,
        sortModel: gridState.sortedColumn,
      });
    },
    [dispatch, getSearchResults, gridState.sortedColumn],
  );

  const xGridSortModel = useMemo(() => toGridSortModel(gridState.sortedColumn), [gridState.sortedColumn]);

  const onSortModelChange = useCallback(
    (model: ReturnType<typeof toGridSortModel>) => {
      const sortedColumn = fromGridSortModel(model);
      const first = model?.[0];

      if (first?.field && !isServerSortableField(first.field)) {
        return;
      }

      dispatch(gridStateActions.setSortedColumn(sortedColumn ?? null));
      dispatch(gridStateActions.setPageNumber(1));
      getSearchResults({
        pageNumber: 1,
        pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
        sortModel: sortedColumn,
      });
    },
    [dispatch, getSearchResults, gridState.pageSize],
  );

  const handleRefresh = useCallback(() => {
    getSearchResults({
      pageNumber: gridState.pageNumber || 1,
      pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
      sortModel: gridState.sortedColumn,
    });
  }, [getSearchResults, gridState.pageNumber, gridState.pageSize, gridState.sortedColumn]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(filter => onApplyFilter(filter))}>
        <Box
          id="tasks-page-outer"
          className={classes.root}
          sx={{ backgroundColor: theme.palette.framework.system.whisper }}
        >
          <Paper
            id="tasks-page-paper"
            elevation={0}
            sx={{
              backgroundColor: 'white',
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '4px',
              padding: '12px',
              minHeight: 'auto',
            }}
          >
            <LayoutGrid
              container
              direction="row"
              justifyContent="space-between"
              classes={{ container: classes.container }}
            >
              <LayoutGridItem
                container
                direction="column"
                spacing={2}
                classes={{ root: classes.mainPanelRoot, container: classes.container }}
              >
                <LayoutGridItem id="tasks-page-filters" xs={12}>
                  <ModsLinkTaskSearchFilters defaultFilterValues={absoluteDefaultValues} />
                </LayoutGridItem>

                <LayoutGridItem id="tasks-page-chips" xs={12}>
                  <SearchTasksChips
                    onApplyFilter={onApplyFilter}
                    gridTag={gridTag}
                    hideDelete={false}
                    setChipExpanded={() => {}}
                    chipLabelPrefixes={{
                      [TaskSearchFilterName.status]: 'Status',
                      [TaskSearchFilterName.candidateAggregate]: 'Candidate',
                      [TaskSearchFilterName.unit]: 'Unit',
                      [TaskSearchFilterName.createdBy]: 'From',
                      [TaskSearchFilterName.recipients]: 'Recipients',
                      [taskSearchFilterName.facility]: 'Facility',
                      [taskSearchFilterName.oid]: 'OID',
                      [taskSearchFilterName.pid]: 'PID',
                      sentDateRange: 'Sent Date',
                      followUpFilter: 'Follow-Up',
                      followUpDateRange: 'Follow-Up Date',
                      priorities: 'Priority',
                      optionApprovalFilter: 'Option Approval',
                      [TaskSearchFilterName.taskCategory]: 'Category',
                      flaggedFilter: 'Flagged',
                      coverageFilter: 'Coverage',
                    }}
                  />
                </LayoutGridItem>

                <LayoutGridItem id="tasks-page-content" xs={12}>
                  <ModsLinkTaskSelectionContext.Provider value={modsLinkTaskSelectionValue}>
                    <DataGridPro
                      apiRef={apiRef}
                      treeData
                      getTreeDataPath={getTreeDataPath}
                      rows={rows}
                      columns={columns}
                      loading={loading}
                      density="compact"
                      getRowClassName={params => (params.row?.isRootNode ? 'tasks-root-row' : '')}
                      disableColumnFilter
                      disableColumnMenu
                      disableChildrenSorting
                      pagination
                      paginationMode="server"
                      sortingMode="server"
                      sortModel={xGridSortModel}
                      onSortModelChange={onSortModelChange}
                      page={currentPage}
                      pageSize={currentPageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageOptions={[50, 100, 150, 200, 250]}
                      rowCount={rowCount}
                      onRowClick={params => {
                        handleSelectTask(params.row);
                      }}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={model => {
                        dispatch(gridStateActions.setColumnVisibilityModel(model));
                        dispatch(
                          userDevicePreferenceActions.saveUserGridPreferenceAction({
                            id: gridTag,
                            value: {
                              ...(gridPreferences?.value || {}),
                              columnVisibilityModel: model,
                            },
                          }),
                        );
                        setCustomColumns(hasCustomisedColumns(model));
                      }}
                      groupingColDef={groupingColDef}
                      components={{
                        Pagination: () => <ModsLinkTaskSearchGridPagination loading={loading} />,
                        Toolbar: XGridToolbar,
                      }}
                      componentsProps={{
                        toolbar: {
                          title: (
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                              {`Tasks (${rowCount})`}
                            </Typography>
                          ),
                          refreshButton: true,
                          refreshGrid: handleRefresh,
                          columnSelector: true,
                          resetButton: customColumns,
                          resetColumns: handleResetColumns,
                          moreOption: true,
                          exportButton: true,
                          filterButton: false,
                          setColumnsButtonEl: () => {},
                          setFilterButtonEl: () => {},
                          panelOpen: '',
                          leftActions: [
                            {
                              KeyId: 'expand-all',
                              Icon: () => null,
                              renderer: () => (
                                <Button
                                  variant="text"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: theme.palette.primary.main,
                                    minWidth: 0,
                                  }}
                                >
                                  Expand All
                                </Button>
                              ),
                              onClick: () => {
                                const allIds = apiRef.current?.getAllRowIds();
                                allIds?.forEach(id => {
                                  apiRef.current?.setRowChildrenExpansion(id, true);
                                });
                              },
                            } as IXGridBulkActionButton,
                            {
                              KeyId: 'collapse-all',
                              Icon: () => null,
                              renderer: () => (
                                <Button
                                  variant="text"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    color: theme.palette.primary.main,
                                    minWidth: 0,
                                  }}
                                >
                                  Collapse All
                                </Button>
                              ),
                              onClick: () => {
                                const allIds = apiRef.current?.getAllRowIds();
                                allIds?.forEach(id => {
                                  apiRef.current?.setRowChildrenExpansion(id, false);
                                });
                              },
                            } as IXGridBulkActionButton,
                          ],
                        },
                      }}
                      sx={{
                        border: '1px solid rgba(224, 224, 224, 1)',
                        fontSize: '14px',
                        height: 'min(60vh, 560px)',
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          fontSize: '14px',
                          fontWeight: 400,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          backgroundColor: theme.palette.framework.system.whisper,
                          fontSize: '12px',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontSize: '12px',
                        },
                        '& .tasks-root-row': {
                          backgroundColor: '#006FB914',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiDivider-root': {
                          height: '16px',
                          alignSelf: 'center',
                        },
                        '& .MuiChip-root': {
                          borderWidth: '1px !important',
                          width: 'auto !important',
                          maxWidth: 'none !important',
                        },
                        '& .MuiChip-root .MuiChip-label': {
                          paddingLeft: '12px !important',
                          paddingRight: '12px !important',
                        },
                      }}
                    />
                  </ModsLinkTaskSelectionContext.Provider>
                </LayoutGridItem>
              </LayoutGridItem>
            </LayoutGrid>
          </Paper>
        </Box>
      </form>
    </FormProvider>
  );
};
